import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaHandshake, FaCheck, FaBrain, FaCode, FaCogs, FaYoutube, FaEnvelope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { usePortfolio } from '../context/PortfolioContext';
import { API_BASE_URL } from '../config/apiConfig';

const CollaborateSection = () => {
  const { portfolioData, loading } = usePortfolio();
  const messages = portfolioData?.collaborate || [];
  const testimonials = (portfolioData?.testimonials || []).filter(t => t.visible);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const navigate = useNavigate();
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (!testimonials.length) return;
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/messages/services`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setServices(data.services);
      });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    if (formData.name === 'dhiraj_admin_enter' && formData.message === '9448277147') {
      // Call backend for admin login
      try {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: formData.name,
            message: formData.message
          }),
        });
        const data = await response.json();
        if (data.success) {
          navigate('/admin');
        } else {
          setSubmitStatus('error');
          setTimeout(() => setSubmitStatus(null), 3000);
        }
      } catch (error) {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus(null), 3000);
      }
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          service: formData.service,
          message: formData.message,
          created_at: new Date().toISOString()
        }),
      });
      const data = await response.json();
      console.log('[LOGIN RESPONSE]', data);
      if (data?.success || data?.user) {
        setSubmitStatus('success');
        setTimeout(() => {
          setFormData({ name: '', email: '', service: '', message: '' });
          setSubmitStatus(null);
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
    }
  };

  const renderTestimonials = () => {
    if (loading) {
      return (
        <div className="animate-pulse bg-gray-800 p-6 rounded-lg h-64">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-14 w-14 bg-gray-700 rounded-full"></div>
            <div>
              <div className="h-4 bg-gray-700 w-40 rounded mb-2"></div>
              <div className="h-3 bg-gray-700 w-32 rounded"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-700 w-full rounded"></div>
            <div className="h-3 bg-gray-700 w-full rounded"></div>
            <div className="h-3 bg-gray-700 w-full rounded"></div>
            <div className="h-3 bg-gray-700 w-3/4 rounded"></div>
          </div>
        </div>
      );
    }
    return (
      <div className="relative h-64 overflow-hidden">
        {testimonials.map((testimonial, index) => (
          <motion.div
            key={testimonial.id}
            className="absolute top-0 left-0 w-full h-full bg-gray-800/80 p-6 rounded-lg"
            initial={{ opacity: 0, x: 50 }}
            animate={{
              opacity: index === activeTestimonial ? 1 : 0,
              x: index === activeTestimonial ? 0 : 50,
              zIndex: index === activeTestimonial ? 10 : 0,
            }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <div className="flex items-center gap-4 mb-4">
              <img
                src={testimonial.image}
                alt={testimonial.name}
                className="h-14 w-14 rounded-full object-cover border-2 border-primary"
              />
              <div>
                <h4 className="text-white font-medium">{testimonial.name}</h4>
                <p className="text-gray-400 text-sm">{testimonial.position}</p>
              </div>
            </div>
            <p className="text-gray-300 italic">&ldquo;{testimonial.text}&rdquo;</p>
          </motion.div>
        ))}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`h-2 w-2 rounded-full transition-all ${
                index === activeTestimonial ? 'bg-primary w-6' : 'bg-gray-600'
              }`}
              onClick={() => setActiveTestimonial(index)}
              aria-label={`View testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="collaborate" className="py-20 bg-gray-900">
      <div className="container">
        <motion.div 
          className="flex items-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <FaHandshake className="text-primary text-2xl" />
          <h2 className="section-title text-white">Let's Collaborate</h2>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column: Why Work With Me */}
          <motion.div
            className="flex flex-col justify-between"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">Why Work With Me</h3>
              <ul className="space-y-4 mb-8">
                <motion.li 
                  className="flex items-start gap-3"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <span className="bg-primary/20 p-2 rounded-full text-primary mt-1">
                    <FaBrain />
                  </span>
                  <div>
                    <h4 className="text-white font-medium">AI + Full-Stack Developer</h4>
                    <p className="text-gray-400">Creating intelligent applications with modern tech stacks and AI integration</p>
                  </div>
                </motion.li>
                <motion.li 
                  className="flex items-start gap-3"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <span className="bg-primary/20 p-2 rounded-full text-primary mt-1">
                    <FaYoutube />
                  </span>
                  <div>
                    <h4 className="text-white font-medium">Business Consultant</h4>
                    <p className="text-gray-400">Providing strategic guidance to help businesses scale and optimize operations</p>
                  </div>
                </motion.li>
                <motion.li 
                  className="flex items-start gap-3"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <span className="bg-primary/20 p-2 rounded-full text-primary mt-1">
                    <FaCogs />
                  </span>
                  <div>
                    <h4 className="text-white font-medium">Innovation Expert</h4>
                    <p className="text-gray-400">Combining mechanical engineering with cutting-edge technology for unique solutions</p>
                  </div>
                </motion.li>
              </ul>
            </div>
            {/* Testimonials */}
            <div className="mt-12">
              <h4 className="text-white font-medium mb-6">What Clients Say</h4>
              {renderTestimonials()}
            </div>
          </motion.div>
          {/* Right Column: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-gray-800/50 rounded-lg p-8">
              <h3 className="text-2xl font-semibold text-white mb-6">Get in Touch</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-700 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-white mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-700 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="service" className="block text-white mb-2">Service Interested In</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-700 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, idx) => (
                      <option key={idx} value={service}>{service}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-white mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full bg-gray-700 text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Tell me about your project..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className={`w-full btn btn-primary flex items-center justify-center gap-2 ${
                    submitStatus === 'loading' ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {submitStatus === 'loading' ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : submitStatus === 'success' ? (
                    <>
                      <FaCheck />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <FaEnvelope />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CollaborateSection; 